import { useState, useEffect } from 'react'
import { useLatest } from '../useLatest'
import { useParams, useSearchParams, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useBruktbilFilters } from '../useBruktbilFilters'
import ExportButtons from '../../../components/ExportButtons'
import type { ColumnDefinition } from '../../../utils/exportToCsv'
import {
  fetchMerkeDetalj,
  fetchModellDetalj,
  actorTypeLabel,
  type ActorType,
  type MerkeDetaljResponse,
} from './detalj_api'

const PAGE_SIZE = 50

function formatAndel(andel: number): string {
  return `${(andel * 100).toLocaleString('nb-NO', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} %`
}

function actorLabel(actorType: string): string {
  return actorTypeLabel(actorType) ?? actorType
}

/**
 * Markedsandel per forhandler for one merke (#489 AC5) or one merke+modell
 * pair (AC6) — one component behind both routes, because the two pages differ
 * only in whether a modell is pinned.
 *
 * The actor_type filter narrows the *seller* side: a PERSON seller is not a
 * forhandler, so leaving them in makes a dealer ranking that is not one. The
 * breakdown above the table is deliberately NOT narrowed by the filter — it is
 * the legend the filter is picked from, and collapsing it to a single 100% row
 * would leave no way back to the other types.
 */
export default function MerkeDetaljerPage() {
  const { t } = useTranslation('bruktbil')
  const { merke, modell } = useParams<{ merke: string; modell?: string }>()
  const filters = useBruktbilFilters()
  const [searchParams] = useSearchParams()

  const [data, setData] = useState<MerkeDetaljResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [notFound, setNotFound] = useState(false)
  const [actorType, setActorType] = useState<ActorType | null>(null)
  const [page, setPage] = useState(1)

  const f = {
    from: filters.from, to: filters.to, klasse: filters.klasse,
    varebil: filters.varebil, omregistrering: filters.omregistrering,
    selgerPostnummer: filters.selgerPostnummer, kjoperPostnummer: filters.kjoperPostnummer,
  }

  // A new actor_type or a changed global filter invalidates the page number.
  useEffect(() => {
    setPage(1)
  }, [actorType, filters.from, filters.to, JSON.stringify(filters.klasse), filters.varebil,
      filters.omregistrering, filters.selgerPostnummer, filters.kjoperPostnummer])

  useLatest(alive => {
    if (!merke) return
    setLoading(true)
    setNotFound(false)
    const opts = { actorType, limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE }
    const req = modell
      ? fetchModellDetalj(merke, modell, f, opts)
      : fetchMerkeDetalj(merke, f, opts)
    req
      .then(r => { if (alive()) setData(r.data) })
      .catch(err => {
        // 404 = this merke/modell has no transfers under the current filters.
        // Anything else is a real failure and must not be dressed up as one.
        if (alive() && err?.response?.status === 404) {
          setData(null)
          setNotFound(true)
        }
      })
      .finally(() => { if (alive()) setLoading(false) })
  }, [merke, modell, actorType, page, filters.from, filters.to, JSON.stringify(filters.klasse),
      filters.varebil, filters.omregistrering, filters.selgerPostnummer, filters.kjoperPostnummer])

  const backUrl = `/bruktbil/merke-modell?${searchParams.toString()}`
  const tittel = modell ? `${merke} ${modell}` : merke
  const pages = data ? Math.max(1, Math.ceil(data.forhandlere_total / PAGE_SIZE)) : 1
  const startRank = (page - 1) * PAGE_SIZE + 1

  const exportRows = (data?.forhandlere ?? []).map((row, i) => ({ rang: startRank + i, ...row }))
  const exportColumns: ColumnDefinition[] = [
    { key: 'rang', label: t('common.rang') },
    { key: 'org_nr', label: t('orgSammenligning.orgNr') },
    { key: 'org_name', label: t('common.forhandler') },
    { key: 'count', label: t('common.antall'), format: (v: number) => v.toLocaleString('nb-NO') },
    { key: 'andel', label: t('common.markedsandel'), format: (v: number) => formatAndel(v) },
  ]

  return (
    <div className="space-y-4" data-testid="merke-detaljer">
      <Link
        to={backUrl}
        className="inline-flex items-center text-sm text-primary-600 dark:text-primary-400 hover:underline"
      >
        {t('merkeDetaljer.tilbake')}
      </Link>

      <div className="card p-4">
        <p className="font-semibold text-gray-800 dark:text-gray-200" data-testid="detalj-tittel">
          {tittel}
        </p>
        {data && (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {actorType
              ? t('merkeDetaljer.eierskifterMedSelger', {
                n: data.total.toLocaleString('nb-NO'),
                selger: actorLabel(actorType).toLowerCase(),
              })
              : t('merkeDetaljer.eierskifter', { n: data.total.toLocaleString('nb-NO') })}
          </p>
        )}
      </div>

      {notFound && (
        <div data-testid="detalj-ikke-funnet" className="card p-4 text-sm text-gray-500">
          {t('merkeDetaljer.ikkeFunnet', { tittel })}
        </div>
      )}

      {data && (
        <>
          {/* Selger-type — the filter's own legend. Counts are for the whole
           *  merke, so picking one never hides the others. */}
          <div data-testid="actor-type-filter" className="card p-3">
            <span className="block text-xs text-gray-500 dark:text-gray-400 mb-2">
              {t('merkeDetaljer.selgertype')}
            </span>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                data-testid="actor-type-alle"
                onClick={() => setActorType(null)}
                className={`text-xs rounded px-2 py-1 border transition-colors ${
                  actorType === null
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                    : 'border-gray-300 dark:border-dark-border text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-border'
                }`}
              >
                {t('common.alle')}
              </button>
              {data.actor_type_breakdown.map(a => (
                <button
                  key={a.actor_type}
                  type="button"
                  data-testid={`actor-type-${a.actor_type}`}
                  onClick={() => setActorType(a.actor_type as ActorType)}
                  className={`text-xs rounded px-2 py-1 border transition-colors ${
                    actorType === a.actor_type
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                      : 'border-gray-300 dark:border-dark-border text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-border'
                  }`}
                >
                  {actorLabel(a.actor_type)} · {a.count.toLocaleString('nb-NO')} ({formatAndel(a.share)})
                </button>
              ))}
            </div>
          </div>

          <div data-testid="forhandler-andel-table" className="card overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-card">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('merkeDetaljer.markedsandelPerForhandler')}
              </span>
              <ExportButtons
                data={exportRows}
                columns={exportColumns}
                metadata={{
                  title: t('merkeDetaljer.exportTitle', { tittel }),
                  dateRange: `${filters.from} – ${filters.to}`,
                  filters: [
                    t('merkeDetaljer.exportSelgertype', {
                      selger: actorType ? actorLabel(actorType) : t('export.alle'),
                    }),
                    filters.klasse.length
                      ? t('export.klasserValgt', { n: filters.klasse.length })
                      : t('export.klasserAlle'),
                  ],
                }}
              />
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-card">
                  <th className="px-4 py-2 text-left font-medium text-gray-600 dark:text-gray-400">{t('common.rang')}</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-600 dark:text-gray-400">{t('common.forhandler')}</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-600 dark:text-gray-400">{t('common.antall')}</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-600 dark:text-gray-400">{t('common.markedsandel')}</th>
                </tr>
              </thead>
              <tbody>
                {data.forhandlere.map((row, i) => (
                  <tr
                    key={`${row.org_nr ?? 'privat'}-${i}`}
                    data-testid="forhandler-andel-row"
                    className="border-b border-gray-100 dark:border-dark-border"
                  >
                    <td className="px-4 py-2 text-gray-500 tabular-nums">{startRank + i}</td>
                    <td className="px-4 py-2">
                      {/* No org_nr means a private seller — they are part of the
                       *  brand's volume, so hiding them would leave the andel
                       *  column summing to less than 100 % with no explanation. */}
                      {row.org_nr
                        ? (row.org_name ?? row.org_nr)
                        : <span className="text-gray-500 italic">{t('merkeDetaljer.privatpersoner')}</span>}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">{row.count.toLocaleString('nb-NO')}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{formatAndel(row.andel)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {data.forhandlere.length === 0 && (
              <p className="text-center text-gray-500 dark:text-gray-400 py-8">
                {t('merkeDetaljer.ingenMedSelgertype')}
              </p>
            )}

            {pages > 1 && (
              <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400 px-4 py-3 border-t border-gray-200 dark:border-dark-border">
                <span>
                  {t('merkeDetaljer.sideAvForhandlere', {
                    page, pages, total: data.forhandlere_total.toLocaleString('nb-NO'),
                  })}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="px-3 py-1 rounded-sm border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    {t('pagination.forrige')}
                  </button>
                  <button
                    onClick={() => setPage(Math.min(pages, page + 1))}
                    disabled={page === pages}
                    className="px-3 py-1 rounded-sm border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    {t('pagination.neste')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {loading && !data && !notFound && (
        <div className="card p-4 animate-pulse h-40 bg-gray-100 dark:bg-dark-border rounded-sm" />
      )}
    </div>
  )
}
